import React, { useEffect, useState } from "react";
import { Navbar } from "../../components/navbar/Navbar";
import { Booking } from "../../components/booking/Booking";
import { Footer } from "../../components/footer/Footer";
import { Calendar } from "../../components/calendar/Calendar";
import { OverView } from "../../components/overView/OverView";
import { ClientInfo } from "../../components/clientInfo/ClientInfo";
import {
  ReservationStatusModal,
  MODAL_STATE,
} from "../../components/reservationStatusModal/ReservationStatusModal";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import dayjs from "dayjs";
import axios from "axios";
import "./reservation.css";
import {getSelectableEmployees, getServicePrice} from "../../models/employees";
import { validate } from "react-email-validator";

export const Reservation = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(
    () => getSelectableEmployees()[0],
  );
  
  const [timer, setTimer] = useState(null); // State to keep the timer reference
  const [selectedService, setSelectedService] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState(
    [],
  );
  const [servicesTotalPrice, setServicesTotalPrice] = useState(0);
  const [additionalServicesTimeTotal, setAdditionalServicesTimeTotal] =
    useState(0);
  const [clientData, setClientData] = useState({
    fullName: "",
    email: "",
    phone: "",
  });
  const [selectedDateRecord, setSelectedDateRecord] = useState(dayjs());
  const [selectedTimeRecord, setSelectedTimeRecord] = useState("");
  const [serviceTimeTotal, setServiceTimeTotal] = useState(15);

  // Status-modal state machine. The wizard hands off to this once the user
  // clicks "Rezervovat termín" — until they click the link in their email,
  // the reservation is held in pending_rezervace, not the real rezervace table.
  const [modalState, setModalState] = useState(MODAL_STATE.IDLE);
  const [currentToken, setCurrentToken] = useState(null);
  const [failureReason, setFailureReason] = useState(null);

  useEffect(() => {
    // Clear the timer when it is replaced or the component unmounts
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timer]);

  useEffect(() => {
    // Going back to step 1 cancels the booking timeout
    if (step < 2 && timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  }, [step, timer]);

  const resetReservation = () => {
    setStep(1);
    setAdditionalServicesTimeTotal(0);
    setSelectedAdditionalServices([]);
    setServicesTotalPrice(0);
    setServiceTimeTotal(15);
    setSelectedTimeRecord("");
    setClientData({
      fullName: "",
      email: "",
      phone: "",
    });
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  };

  const handleReservationButton = () => {
    setStep(2); // Move to step 2

    const newTimer = setTimeout(() => {
      alert("Čas pro vytvoření rezervace vypršel, začněte prosím znovu.");
      resetReservation();
    }, 300000);

    setTimer(newTimer);
  };

  useEffect(() => {
    setStep(1);
    setAdditionalServicesTimeTotal(0);
    setSelectedAdditionalServices([]);
    setServicesTotalPrice(0);
    setServiceTimeTotal(15);
    setSelectedTimeRecord("");
    setClientData({
      fullName: "",
      email: "",
      phone: "",
    });
  }, []);

  const [bookedDates, setBookedDates] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let fetchReservationsUri = process.env.REACT_APP_API_URL_FETCH;
        if (selectedEmployee) {
          fetchReservationsUri += `?employeeId=${selectedEmployee.id}`;
        }
        const response = await axios.get(fetchReservationsUri);
        const fetchedReservations = response.data.map((item) => ({
          id: item.id,
          date: dayjs(item.datum).format("DD.MM.YYYY"),
          startTime: dayjs(item.zacatek_cas, "HH:mm:ss").format("HH:mm"),
          finishTime: dayjs(item.konec_cas, "HH:mm:ss").format("HH:mm"),
        }));
        setBookedDates(fetchedReservations);
      } catch (err) {
        console.error("Fetching reservations failed: ", err);
      }
    };
    fetchData();
  }, [selectedEmployee, step]);

  useEffect(() => {
    selectedService.nazev
      ? setServiceTimeTotal(
          selectedService.delkaTrvani + additionalServicesTimeTotal,
        )
      : setServiceTimeTotal(additionalServicesTimeTotal);
  }, [selectedService, additionalServicesTimeTotal]);

  const isDateAvailable = (selectedDate, selectedTime, duration) => {
    const selectedStartTime = dayjs(
      selectedDate.format("YYYY-MM-DD") + " " + selectedTime,
      "YYYY-MM-DD HH:mm",
    );
    const selectedEndTime = selectedStartTime.add(duration, "minute");

    return !bookedDates.some((booking) => {
      const bookingStart = dayjs(
        booking.date + " " + booking.startTime,
        "DD.MM.YYYY HH:mm",
      );
      const bookingEnd = dayjs(
        booking.date + " " + booking.finishTime,
        "DD.MM.YYYY HH:mm",
      );

      return (
        selectedStartTime.isBefore(bookingEnd) &&
        selectedEndTime.isAfter(bookingStart)
      );
    });
  };

  // Helpers shared by both submit and resend paths.
  const buildAdditionalServicesString = () =>
    selectedAdditionalServices && selectedAdditionalServices.length > 0
      ? selectedAdditionalServices.map((service) => service.nazev).join(", ")
      : "";

  const computeTotalPrice = () =>
    (getServicePrice(selectedEmployee.id, selectedService) || 0) +
    servicesTotalPrice;

  const submitReservation = async () => {
    // Wizard timer is no longer relevant — the pending row's 15-minute
    // server-side TTL takes over from here.
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }

    setFailureReason(null);
    setModalState(MODAL_STATE.SUBMITTING);

    // Local re-check before we even hit the server.
    if (
      !isDateAvailable(selectedDateRecord, selectedTimeRecord, serviceTimeTotal)
    ) {
      setFailureReason("SLOT_TAKEN");
      setModalState(MODAL_STATE.INITIAL_FAILURE);
      return;
    }

    // Cosmetic: show "Odesíláme email…" after a short delay so the user
    // sees two distinct stages even though the server handles both atomically.
    const sendingEmailTimer = setTimeout(
      () => setModalState(MODAL_STATE.SENDING_EMAIL),
      800,
    );

    const postData = {
      zamestnanecId: selectedEmployee.id,
      datum: selectedDateRecord.format("YYYY-MM-DD"),
      zacatek_cas: dayjs(selectedTimeRecord, "HH:mm").format("HH:mm:ss.SSS"),
      konec_cas: dayjs(selectedTimeRecord, "HH:mm")
        .add(serviceTimeTotal, "minute")
        .format("HH:mm:ss.SSS"),
      klient_jmeno: clientData.fullName,
      klient_sluzba: selectedService.nazev || "",
      klient_sluzby_dodatecne: buildAdditionalServicesString(),
      klient_telefon: clientData.phone,
      klient_email: clientData.email,
      klient_cena: computeTotalPrice(),
      employeeName: selectedEmployee.name,
      duration: serviceTimeTotal,
    };

    let pendingResponse;
    try {
      pendingResponse = await axios.post(
        process.env.REACT_APP_API_URL_CREATE_PENDING,
        postData,
        { headers: { "Content-Type": "application/json" } },
      );
    } catch (err) {
      clearTimeout(sendingEmailTimer);
      console.error("Creating pending reservation failed:", err);
      setFailureReason("EMAIL_FAILED");
      setModalState(MODAL_STATE.INITIAL_FAILURE);
      return;
    }

    clearTimeout(sendingEmailTimer);

    if (!pendingResponse.data || !pendingResponse.data.success) {
      const code = pendingResponse.data && pendingResponse.data.code;
      setFailureReason(code === "SLOT_TAKEN" ? "SLOT_TAKEN" : "EMAIL_FAILED");
      setModalState(MODAL_STATE.INITIAL_FAILURE);
      return;
    }

    const token = pendingResponse.data.token;
    setCurrentToken(token);

    if (pendingResponse.data.emailSent) {
      setModalState(MODAL_STATE.EMAIL_SENT);
    } else {
      setFailureReason("EMAIL_FAILED");
      setModalState(MODAL_STATE.INITIAL_FAILURE);
    }
  };

  const handleResend = async () => {
    if (!currentToken) {
      setModalState(MODAL_STATE.RESEND_FAILED);
      return;
    }

    setModalState(MODAL_STATE.RESENDING);

    let resendResponse;
    try {
      resendResponse = await axios.post(
        process.env.REACT_APP_API_URL_RESEND_TOKEN,
        {
          token: currentToken,
          employeeName: selectedEmployee.name,
          duration: serviceTimeTotal,
        },
        { headers: { "Content-Type": "application/json" } },
      );
    } catch (err) {
      console.error("Resend token request failed:", err);
      setModalState(MODAL_STATE.RESEND_FAILED);
      return;
    }

    if (!resendResponse.data || !resendResponse.data.success) {
      setModalState(MODAL_STATE.RESEND_FAILED);
      return;
    }

    const newToken = resendResponse.data.token;
    setCurrentToken(newToken);

    if (resendResponse.data.emailSent) {
      setModalState(MODAL_STATE.EMAIL_RESENT);
    } else {
      setModalState(MODAL_STATE.RESEND_FAILED);
    }
  };

  const handleModalReset = () => {
    setModalState(MODAL_STATE.IDLE);
    setCurrentToken(null);
    setFailureReason(null);
    resetReservation();
  };

  // Warn the user if they try to leave while the reservation is pending —
  // they would lose the resend option from this tab.
  useEffect(() => {
    const isHoldingPendingReservation =
      modalState === MODAL_STATE.EMAIL_SENT ||
      modalState === MODAL_STATE.EMAIL_RESENT ||
      modalState === MODAL_STATE.RESENDING;

    if (!isHoldingPendingReservation) return undefined;

    const handler = (e) => {
      e.preventDefault();
      // Required for legacy browsers; the actual message text is ignored
      // by modern browsers, which show their own generic prompt.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [modalState]);

  // Snapshot of the current selection passed into the modal so the user can
  // see what they're confirming. Reads directly from wizard state.
  const reservationSummary = selectedService
    ? {
        employeeName: selectedEmployee?.name,
        date: selectedDateRecord?.format("DD.MM.YYYY"),
        time: selectedTimeRecord,
        service: selectedService?.nazev,
        additionalServices: buildAdditionalServicesString(),
        totalPrice: computeTotalPrice(),
      }
    : null;

  return (
    <div className="reservation">
      <div className="pageWrapper">
        <Navbar />
        
        <div className="bodyContent">
          {step > 1 && (
            <ArrowBackIosNewIcon
              style={{ height: "5rem", width: "5rem" }}
              className="arrowBackIcon desktop-only"
              onClick={() => {
                setStep(step - 1);
                setAdditionalServicesTimeTotal(0);
              }}
              data-aos="fade-right"
              data-aos-duration="1200"
            />
          )}
          {step === 1 && (
            <div className="reservationSection">
              <div
                className="reservationFrame mobileFullWidth"
                data-aos="fade-down"
                data-aos-duration="1200"
              >
                <div style={{marginBottom: "15px"}}>
                <Booking
                  selectedEmployee={selectedEmployee}
                  setSelectedEmployee={setSelectedEmployee}
                  setSelectedService={setSelectedService}
                  selectedService={selectedService}
                  setServicesTotalPrice={setServicesTotalPrice}
                  selectedAdditionalServices={selectedAdditionalServices}
                  setSelectedAdditionalServices={setSelectedAdditionalServices}
                  setAdditionalServicesTimeTotal={
                    setAdditionalServicesTimeTotal
                  }
                />
                </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  className="reserveBtn"
                  onClick={handleReservationButton}
                  disabled={
                    !(selectedService || selectedAdditionalServices.length > 0)
                  }
                >
                  Vybrat termín
                </button>
                </div>
              </div>
              
            </div>
          )}
          {step === 2 && (
            <div className="reservationSection">
              <div
                className="reservationFrame"
                data-aos="fade-down"
                data-aos-duration="1200"
              >
                <Calendar
                  selectedEmployee={selectedEmployee}
                  setSelectedDateRecord={setSelectedDateRecord}
                  setSelectedTimeRecord={setSelectedTimeRecord}
                  serviceTimeTotal={serviceTimeTotal}
                  additionalServicesTimeTotal={additionalServicesTimeTotal}
                  bookedDates={bookedDates}
                  setStep={setStep}
                  selectedService={selectedService}
                  selectedAdditionalServices={selectedAdditionalServices}
                  servicesTotalPrice={servicesTotalPrice}
                />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="reservationSection">
              <div
                className="reservationFrame"
                data-aos="fade-down"
                data-aos-duration="1200"
              >
                <ClientInfo setClientData={setClientData} />
                <OverView
                  selectedEmployee={selectedEmployee}
                  servicesTotalPrice={servicesTotalPrice}
                  serviceTimeTotal={serviceTimeTotal}
                  selectedAdditionalServices={selectedAdditionalServices}
                  selectedService={selectedService}
                  selectedTimeRecord={selectedTimeRecord}
                  selectedDateRecord={selectedDateRecord}
                />
                <span className="cancelReservation">
                  Rezervaci prosím zrušte nejpozději 2 hodiny před sjednaným
                  termínem telefonicky nebo prostřednictvím SMS na číslo 777 606
                  447.
                  <br />
                  <b>
                    V případě neomluvené absence nebo zrušení rezervace méně než
                    2 hodiny předem bude při příští návštěvě účtován poplatek ve
                    výši 50% z ceny služby.
                  </b>
                </span>

                <p
                  style={{
                    fontSize: "14px",
                    color: "black",
                    fonstStyle: "italic",
                    padding: "0px 20px",
                  }}
                >
                  Kliknutím na tlačítko „Rezervovat“ souhlasíte s tím, že Barber
                  Shop CJ's Barbershop bude zpracovávat vaše osobní údaje
                  (jméno, telefonní číslo a e-mailová adresa) za účelem
                  realizace rezervace služby a dalších souvisejících
                  komunikačních účelů. Tyto údaje budou uchovávány v souladu s
                  našimi interními pravidly pro ochranu osobních údajů a nebudou
                  poskytovány třetím stranám bez vašeho výslovného souhlasu.
                </p>
                  {(clientData.email === "" || validate(clientData.email)) ||
                      <caption style={{ color: "red", fontSize: "12px", fontWeight: 500, padding: '5px' }}>Nelze odeslat - Nesprávný formát emailu</caption>}
                <button
                  className="reserveBtn"
                  type="button"
                  onClick={submitReservation}
                  disabled={
                    !(
                      clientData.fullName &&
                      validate(clientData.email) &&
                      clientData.phone
                    )
                  }
                >
                  Rezervovat termín
                </button>
              </div>
            </div>
          )}
        </div>
        <footer>
          <Footer />
        </footer>
      </div>
      <ReservationStatusModal
        state={modalState}
        onResend={handleResend}
        onReset={handleModalReset}
        reservationSummary={reservationSummary}
        failureReason={failureReason}
      />
    </div>
  );
};
